/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { forwardRef, useId, useState } from "react";
import { cn } from "../../lib/utils";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  label?: string;
  error?: string;
  prefix?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  onSuffixIconClick?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      id,
      error,
      prefix,
      suffixIcon,
      onSuffixIconClick,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const hasError = !!error;

    return (
      <div className={cn("space-y-1.5", !label && "w-full")}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "block text-sm font-medium transition-colors duration-200",
              hasError
                ? "text-error"
                : isFocused
                  ? "text-interactive"
                  : "text-text-secondary"
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {prefix && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              {prefix}
            </span>
          )}
          <input
            id={inputId}
            aria-invalid={hasError || undefined}
            aria-describedby={hasError ? errorId : undefined}
            className={cn(
              "flex h-11 w-full rounded-2xl border bg-bg-elevated px-3 py-2 text-base transition-all duration-200",
              "focus:outline-none text-text-primary",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "placeholder:text-text-tertiary",
              hasError
                ? "border-error focus:border-error focus:ring-2 focus:ring-error/20"
                : "border-border hover:border-border-hover focus:border-interactive focus:ring-2 focus:ring-interactive/20",
              prefix ? "pl-7" : "",
              suffixIcon ? "pr-10" : "",
              className
            )}
            ref={ref}
            value={value}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={onChange}
            {...props}
          />

          {suffixIcon && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-interactive focus:outline-none transition-colors duration-200"
              onClick={onSuffixIconClick}
            >
              {suffixIcon}
            </button>
          )}
        </div>

        {error && (
          <p id={errorId} className="text-sm text-error font-medium">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
